import { Avatar, Grid } from '@mui/material';
import BoyImage from '../../../images/boy.png';
import HealthStatBox from './HealthStatBox';
import '../../Caregivers/caregivers.css';
import useStore from '../../../Store/Store';
import React, { useEffect, useState } from 'react';
import { getWalkActivity } from '../../../utils/api';
import { useTranslation } from 'react-i18next';
const HealthCard = () => {
  // Only select the specific pieces of state you need
  const user = useStore((state) => state.user);
  const selectedRecipient = useStore((state) => state.selectedRecipient);
  const careRecipients = useStore((state) => state.careRecipients);
  const [steps, setSteps] = useState(0);
  const { t } = useTranslation();
  // Memoize the user data calculation
  const userToDisplay = React.useMemo(() => {
    if (!user || !careRecipients) return null;

    return selectedRecipient && careRecipients[selectedRecipient]
      ? careRecipients[selectedRecipient]?.careRecipientId?.userInfo
      : user?.userInfo;
  }, [selectedRecipient, careRecipients, user]);

  // Fetch walking activity data
  useEffect(() => {
    const fetchSteps = async () => {
      try {
        if (!userToDisplay) return;
        const userId = selectedRecipient || user?.id;
        const deviceId =
          selectedRecipient && careRecipients[selectedRecipient]
            ? careRecipients[selectedRecipient]?.careRecipientId?.deviceId
            : user?.deviceId;
        if (!userId) return;

        const walkData = await getWalkActivity({ userId, deviceId });
        if (walkData && walkData.data.STEPS) {
          setSteps(walkData.data.STEPS);
        }
      } catch (error) {
        console.error('Error fetching walking data:', error);
      }
    };
    fetchSteps();
  }, [selectedRecipient, user]);

  // Memoize the user info object
  const userInfo = React.useMemo(() => {
    if (!userToDisplay)
      return {
        height: 'N/A',
        weight: 'N/A',
        gender: 'N/A',
        age: '0',
        name: 'N/A',
        imageUrl: BoyImage,
      };

    return {
      height: userToDisplay.height || 'N/A',
      weight: userToDisplay.weight || 'N/A',
      gender: userToDisplay.gender || 'N/A',
      age: userToDisplay.age || '0',
      name: `${userToDisplay.firstName || ''} ${userToDisplay.lastName || ''}`.trim() || 'N/A',
      imageUrl: userToDisplay.imageUrl || BoyImage,
    };
  }, [userToDisplay]);

  // Memoize these conversion functions
  const convertHeight = React.useCallback((height) => {
    if (height === 'N/A' || !height) return 'N/A';
    try {
      const [feet, inches] = height.split("'").map((str) => parseInt(str.replace(/[^0-9]/g, '')));
      return Math.round(feet * 30.48 + inches * 2.54);
    } catch (error) {
      console.error('Error converting height:', error);
      return 'N/A';
    }
  }, []);

  const convertWeight = React.useCallback((weight) => {
    if (weight === 'N/A' || !weight) return 'N/A';
    try {
      const lbs = parseInt(weight.replace(/[^0-9]/g, ''));
      return Math.round(lbs * 0.45359237);
    } catch (error) {
      console.error('Error converting weight:', error);
      return 'N/A';
    }
  }, []);

  const calculateBMI = React.useCallback(
    (weight, height) => {
      if (weight === 'N/A' || height === 'N/A' || !weight || !height) return 'N/A';
      try {
        const weightInKg = convertWeight(weight);
        if (weightInKg === 'N/A') return 'N/A';

        const heightInCm = convertHeight(height);
        if (heightInCm === 'N/A') return 'N/A';

        const heightInMeters = heightInCm / 100;
        return (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
      } catch (error) {
        console.error('Error calculating BMI:', error);
        return 'N/A';
      }
    },
    [convertHeight, convertWeight],
  );

  // Memoize the calculated values to prevent recalculation on every render
  const weightValue = React.useMemo(() => `${convertWeight(userInfo.weight)} kg`, [convertWeight, userInfo.weight]);

  const heightValue = React.useMemo(() => `${convertHeight(userInfo.height)} cm`, [convertHeight, userInfo.height]);

  const bmiValue = React.useMemo(
    () => calculateBMI(userInfo.weight, userInfo.height),
    [calculateBMI, userInfo.weight, userInfo.height],
  );

  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      style={{
        marginTop: '20px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        width: '100%',
      }}
    >
      <Grid item sm={12} lg={4} md={4} xs={12} container alignItems="center" gap={2}>
        <Avatar
          src={userInfo.imageUrl}
          alt={userInfo.name}
          style={{
            height: '64px',
            width: '64px',
          }}
        />
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>{userInfo.name}</h2>
          <span style={{ color: '#666', fontSize: '14px', display: 'block', marginTop: '4px' }}>
            {userInfo.gender}, {userInfo.age} y.o.
          </span>
          <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>{/* Icons for phone and message */}</div>
        </div>
      </Grid>
      <Grid
        item
        container
        sm={12}
        lg={8}
        md={8}
        xs={12}
        spacing={2}
        style={{ display: 'flex', alignItems: 'center', height: '100%' }}
      >
        <Grid item xs={6} sm={6} md={3} lg={3}>
          <HealthStatBox title={t('weight')} value={weightValue} />
        </Grid>
        <Grid item xs={6} sm={6} md={3} lg={3}>
          <HealthStatBox title={t('height')} value={heightValue} />
        </Grid>
        <Grid item xs={6} sm={6} md={3} lg={3}>
          <HealthStatBox title={t('bmi')} value={bmiValue} />
        </Grid>
        <Grid item xs={6} sm={6} md={3} lg={3}>
          <HealthStatBox title={t('steps')} value={steps} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default HealthCard;
